import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Shield, Compass, Map, Zap } from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import globalHeartbeat from '../utils/globalHeartbeatAudio';

/**
 * SpiralOrg Final Landing Page
 * Video-first design with professional typography
 * Clean, sophisticated, mobile-friendly
 */
const SpiralOrgFinalLanding = ({ onExploreMembers, onBeginJourney, onEnterProjects, onSwitchToCinematic }) => {
  const audioRef = useRef(null);

  // Initialize heartbeat and audio
  useEffect(() => {
    if (window.planetaryFedHeartbeat) {
      window.planetaryFedHeartbeat.setEnabled(true);
      
      const startAudio = () => {
        if (window.planetaryFedHeartbeat.audioContext?.state === 'suspended') {
          window.planetaryFedHeartbeat.audioContext.resume().then(() => {
            window.planetaryFedHeartbeat.forceRestart();
          });
        } else {
          window.planetaryFedHeartbeat.forceRestart();
        }
        
        // Also start background audio loop
        if (audioRef.current) {
          audioRef.current.play().catch(e => console.log('Audio autoplay blocked:', e));
        }
      };

      document.addEventListener('click', startAudio, { once: true });
      return () => document.removeEventListener('click', startAudio);
    }
  }, []);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden relative">

      {/* Background Audio Loop - Keep the pretty song */}
      <audio ref={audioRef} loop preload="auto">
        <source src="/audio-loop.mp4" type="audio/mp4" />
      </audio>

      {/* Spiral Tree Animation - FULL SCREEN at ANY RESOLUTION with Smooth Loop */}
      <div className="fixed inset-0 z-10 pointer-events-none overflow-hidden">
        <video 
          ref={audioRef}
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto video-smooth-loop"
          style={{ 
            filter: 'brightness(1.1) contrast(1.2) saturate(1.15)',
            objectFit: 'cover',
            objectPosition: 'center center',
            transform: 'translate(-50%, -50%) scale(1.3)',
            transition: 'opacity 0.5s ease-in-out'
          }}
          onLoadedMetadata={(e) => {
            e.target.playbackRate = 1.0; // 100% normal speed
            
            // Add smooth loop transition
            const video = e.target;
            video.addEventListener('timeupdate', () => {
              const duration = video.duration;
              const currentTime = video.currentTime;
              
              // Fade out in last 0.5 seconds
              if (duration - currentTime < 0.5) {
                video.style.opacity = (duration - currentTime) / 0.5;
              }
              // Fade in during first 0.5 seconds
              else if (currentTime < 0.5) {
                video.style.opacity = currentTime / 0.5;
              }
              // Full opacity in middle
              else {
                video.style.opacity = 1;
              }
            });
          }}
        >
          <source src="/spiral-tree-animation.mov" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-20">
        {/* Hero - Split Layout: Top Third and Bottom Third */}
        <section className="min-h-screen flex flex-col justify-between px-6 py-16">
          {/* TOP THIRD - Title and Original Tagline */}
          <div className="text-center max-w-5xl mx-auto pt-8">
            {/* Main Title */}
            <h1 
              className="mb-8"
              style={{ 
                fontFamily: "'Cinzel', serif",
                fontSize: 'clamp(3.5rem, 10vw, 8rem)',
                fontWeight: '400',
                letterSpacing: '0.12em',
                lineHeight: '1.1',
                textShadow: '0 4px 20px rgba(0, 0, 0, 0.9), 0 0 60px rgba(0, 0, 0, 0.7), 0 2px 4px rgba(0, 0, 0, 0.8)',
                color: '#FFFFFF'
              }}
            >
              SpiralOrg
            </h1>

            {/* Professional Subtitle - Emphasize "and" */}
            <p 
              className="text-2xl md:text-3xl font-light leading-relaxed text-white max-w-3xl mx-auto"
              style={{ 
                fontFamily: "'Cormorant Garamond', serif",
                letterSpacing: '0.02em',
                textShadow: '0 3px 15px rgba(0, 0, 0, 0.9), 0 1px 3px rgba(0, 0, 0, 0.8)',
                fontWeight: '500'
              }}
            >
              A Guild of Families Building Belonging<br/>
              Through Logistics <span className="font-normal italic text-4xl" style={{ textShadow: '0 3px 15px rgba(0, 0, 0, 0.9)' }}>and</span> Song.
            </p>
          </div>

          {/* MIDDLE THIRD - Empty to show video */}
          <div className="flex-1"></div>

          {/* BOTTOM THIRD - Original Copy + Buckets */}
          <div className="text-center max-w-5xl mx-auto pb-8">
            {/* Original Description - Revised Copy */}
            <p 
              className="text-lg md:text-xl font-medium mb-8 leading-relaxed text-white max-w-3xl mx-auto"
              style={{ 
                fontFamily: "'Cormorant Garamond', serif",
                letterSpacing: '0.01em',
                textShadow: '0 2px 12px rgba(0, 0, 0, 0.95), 0 1px 3px rgba(0, 0, 0, 0.9)'
              }}
            >
              An <span className="font-semibold">old-school spiral organizer</span> of people and projects, activating 
              bliss ventures, retreats, missions and purpose — the way nature does — in <span className="font-semibold">organic 
              spirals of interconnection, coherence and confluence</span>.
            </p>

            <p 
              className="text-base font-medium mb-12 leading-relaxed text-white/95 max-w-2xl mx-auto italic"
              style={{ 
                fontFamily: "'Cormorant Garamond', serif",
                textShadow: '0 2px 12px rgba(0, 0, 0, 0.95), 0 1px 3px rgba(0, 0, 0, 0.9)'
              }}
            >
              It's part digital tool, part human federation:<br/>
              a planner, a <span className="font-semibold">mentor alliance</span>, and a movement all in one.
            </p>

            <p 
              className="text-xl md:text-2xl font-medium mb-12 leading-relaxed text-white max-w-3xl mx-auto"
              style={{ 
                fontFamily: "'Cinzel', serif",
                letterSpacing: '0.05em',
                textShadow: '0 3px 15px rgba(0, 0, 0, 0.95), 0 1px 4px rgba(0, 0, 0, 0.9)'
              }}
            >
              How Can SpiralOrg Help Activate Your Org?
            </p>

            {/* CTAs + First 100 Call */}
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={onBeginJourney}
                  className="px-6 py-2 bg-[#E6C685]/10 border border-[#E6C685]/40 text-[#E6C685] rounded-full 
                    text-sm font-light tracking-wide hover:bg-[#E6C685]/20 transition-all duration-300"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  Begin My Journey
                </button>
                
                <button
                  onClick={onEnterProjects}
                  className="px-6 py-2 border border-[#CED3CC]/30 text-[#CED3CC] rounded-full 
                    text-sm font-light tracking-wide hover:border-[#E6C685]/40 hover:text-[#E6C685] 
                    transition-all duration-300"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  My Projects
                </button>
              </div>
              
              {/* Call to First 100 */}
              <div className="bg-gradient-to-r from-[#5C2D73]/70 to-[#E6C685]/50 backdrop-blur-xl border border-[#E6C685]/50 rounded-2xl p-6 mt-8">
                <p className="text-xs uppercase tracking-widest text-[#E6C685] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  Calling the First 100
                </p>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: "'Cinzel', serif" }}>
                  Early Adopters • Power Users • Cofounders
                </h3>
                <p className="text-white/90 text-sm mb-4 max-w-xl mx-auto leading-relaxed">
                  A simple, lawful way for families to run projects together. The family-reunion OS and species-level cheat code. 
                  If you see it, help shape it.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What This Solves - Benefits First */}
        <section className="relative py-20 px-6 bg-black/20">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#5C2D73]/30 to-black/60 backdrop-blur-2xl border border-[#E6C685]/20 rounded-3xl p-10 text-center">
              <h2 className="text-3xl font-light text-[#E6C685] mb-6" style={{ fontFamily: "'Cinzel', serif" }}>
                What This Solves (In One Breath)
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Coordination fatigue, caregiver burnout, missing stories, unfair costs, scattered documents, fragile housing, 
                and "we should get together more" guilt.
              </p>
              <div className="h-px bg-[#E6C685]/30 my-6"></div>
              <p className="text-lg text-[#E6C685] leading-relaxed font-medium" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                <span className="font-bold">Replaced with:</span> shared calendars, role clarity, trust-credit fairness, a living archive, 
                universal-design venues, and repeatable, music-forward gatherings.
              </p>
            </div>
          </div>
        </section>

        {/* The Three Layers - Moved Above Example */}
        <section className="relative py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-light text-[#E6C685] mb-12 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
              The Organism, The Organizer, The Org
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Organism */}
              <div className="bg-black/60 backdrop-blur-2xl border border-[#E6C685]/20 rounded-2xl p-8 hover:border-[#E6C685]/50 transition-all">
                <div className="text-6xl mb-4 text-center">🌱</div>
                <h3 className="text-2xl font-bold text-[#E6C685] mb-4 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
                  The Organism
                </h3>
                <p className="text-white/80 leading-relaxed mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  People + land. Real families, volunteers, and "traveling faculty" who can spin up a gathering with nothing but a phone and a guitar.
                </p>
                <p className="text-sm text-[#E6C685]/70 italic">
                  The living network of humans and places.
                </p>
              </div>
              
              {/* Organizer */}
              <div className="bg-gradient-to-br from-[#5C2D73]/40 to-black/60 backdrop-blur-2xl border-2 border-[#E6C685]/40 rounded-2xl p-8">
                <div className="text-6xl mb-4 text-center">⚙️</div>
                <h3 className="text-2xl font-bold text-[#E6C685] mb-4 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
                  The Organizer
                </h3>
                <div className="space-y-3 mb-4">
                  <div className="bg-black/40 rounded-lg p-3 border border-[#E6C685]/20">
                    <p className="text-white font-semibold text-sm mb-1">Family Project Management</p>
                    <p className="text-white/70 text-xs">Calendars, budgets, crews, care roles, travel, RSVPs</p>
                  </div>
                  <div className="bg-black/40 rounded-lg p-3 border border-[#E6C685]/20">
                    <p className="text-white font-semibold text-sm mb-1">Lore Library</p>
                    <p className="text-white/70 text-xs">Living archive—voices, images, letters, playlists</p>
                  </div>
                  <div className="bg-black/40 rounded-lg p-3 border border-[#E6C685]/20">
                    <p className="text-white font-semibold text-sm mb-1">Trust Ledger</p>
                    <p className="text-white/70 text-xs">Internal economy for time, transport, caregiving, meals</p>
                  </div>
                </div>
                <button 
                  onClick={onEnterProjects}
                  className="w-full py-3 bg-[#E6C685] text-black rounded-lg font-bold hover:bg-[#E6C685]/80 transition-all"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  Explore the Tools →
                </button>
              </div>
              
              {/* Org */}
              <div className="bg-black/60 backdrop-blur-2xl border border-[#E6C685]/20 rounded-2xl p-8 hover:border-[#E6C685]/50 transition-all">
                <div className="text-6xl mb-4 text-center">🏛️</div>
                <h3 className="text-2xl font-bold text-[#E6C685] mb-4 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
                  The Org
                </h3>
                <p className="text-white/80 leading-relaxed mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Lawful privacy. A Wisconsin <span className="font-semibold text-[#E6C685]">Chapter 184</span> private membership association with purpose-based trust wrappers for land and assets.
                </p>
                <p className="text-sm text-[#E6C685]/70 italic">
                  Public when we want to be, private when we need to be.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Example: How It Works */}
        <section className="relative py-24 px-6 bg-black/20">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-light text-[#E6C685] mb-12 text-center" style={{ fontFamily: "'Cinzel', serif" }}>
              An Example of How This Helps
            </h2>
            
            <div 
              className="bg-gradient-to-br from-[#5C2D73]/40 to-black/60 backdrop-blur-2xl border border-[#E6C685]/20 rounded-3xl p-10"
              style={{ boxShadow: '0 20px 80px rgba(0, 0, 0, 0.8)' }}
            >
              <p 
                className="text-lg leading-relaxed font-light mb-6"
                style={{ 
                  fontFamily: "'Cormorant Garamond', serif",
                  color: '#CED3CC'
                }}
              >
                A daughter wanted to celebrate her aging parent's milestone birthday without turning love into logistical chaos. 
                Using SpiralOrg, the extended family coordinated a small reunion—framed as a celebration, engineered as reconnection. 
                One cousin brought music, others managed food and travel, and the family held a circle where each generation shared gratitude and voiced one concrete need.
              </p>

              <div className="bg-black/40 rounded-2xl p-6 mb-6 border border-[#E6C685]/30">
                <h4 className="text-[#E6C685] font-bold mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                  The entire event ran through the SpiralOrg toolkit:
                </h4>
                <ul className="space-y-3 text-white/90" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  <li className="flex items-start gap-3">
                    <span className="text-[#E6C685] text-xl">✓</span>
                    <span>Invites, carpools, budgets, task assignments, <span className="italic">and</span> a simple "thank-you ledger" lived in one shared space.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#E6C685] text-xl">✓</span>
                    <span>Photos, recipes, stories, and family memories flowed straight into the <span className="font-semibold text-[#E6C685]">Lore Library</span> for future reference.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-[#E6C685] text-xl">✓</span>
                    <span>Every small contribution was tracked as a <span className="italic">micro-win</span>—creating patterns they could repeat regularly, not just once a year.</span>
                  </li>
                </ul>
              </div>

              <p 
                className="text-lg leading-relaxed font-medium text-[#E6C685] italic"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                After the gathering, everything lived on as a Family Project: a clear record of who contributed what, who needed support, and memories that proved it mattered. 
                Stress dropped. Participation increased. People felt valued. Belonging lasted beyond the event itself.
              </p>
              
              <p 
                className="text-xl leading-relaxed font-bold text-white mt-6 text-center"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                This is how family reunions become engines of belonging.
              </p>
            </div>
          </div>
        </section>

        {/* Duplicate removed - keeping the one above Example section */}

        {/* SpiralCred - Trust-Based Economy */}
        <section className="relative py-24 px-6 bg-gradient-to-b from-[#1E2A38] to-black">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-light text-[#E6C685] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                SpiralCred
              </h2>
              <p className="text-2xl text-white font-light mb-2">
                Survive the Collapse. Thrive in Trust.
              </p>
              <p className="text-lg text-[#CED3CC]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                The elegant, intuitive solution for any storm—the person-to-person credit system within SpiralOrg
              </p>
            </div>
            
            {/* SpiralCred Visual Explanation */}
            <div className="mb-16">
              <img 
                src="https://customer-assets.emergentagent.com/job_spiralorg/artifacts/vxq5ntr6_55961C47-FD3B-4504-A6DC-63FAFBD69702.png"
                alt="SpiralCred - The Physics of Trust-Based Exchange"
                className="w-full max-w-4xl mx-auto rounded-2xl shadow-2xl border-2 border-[#E6C685]/40"
                style={{ boxShadow: '0 20px 80px rgba(230, 198, 133, 0.3)' }}
              />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Spiral Flow Diagram */}
              <div className="space-y-8">
                <div className="bg-gradient-to-br from-[#5C2D73]/40 to-black/60 backdrop-blur-xl border border-[#E6C685]/30 rounded-2xl p-6">
                  <h4 className="text-[#E6C685] font-bold text-xl mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                    Collapse → Clarity
                  </h4>
                  <p className="text-white/80 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    When old systems falter, community remains.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-[#5C2D73]/40 to-black/60 backdrop-blur-xl border border-[#E6C685]/30 rounded-2xl p-6">
                  <h4 className="text-[#E6C685] font-bold text-xl mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                    Connection → Trust
                  </h4>
                  <p className="text-white/80 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Each trade creates new money—backed by trust, not debt.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-[#5C2D73]/40 to-black/60 backdrop-blur-xl border border-[#E6C685]/30 rounded-2xl p-6">
                  <h4 className="text-[#E6C685] font-bold text-xl mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                    Flow → Resilience
                  </h4>
                  <p className="text-white/80 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Credits circulate like breath—self-balancing, interest free.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-[#5C2D73]/40 to-black/60 backdrop-blur-xl border border-[#E6C685]/30 rounded-2xl p-6">
                  <h4 className="text-[#E6C685] font-bold text-xl mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                    Prosperity → Regeneration
                  </h4>
                  <p className="text-white/80 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    A living economy rooted in relationship and mutual support.
                  </p>
                </div>
              </div>
              
              {/* CTA & Features */}
              <div className="text-center lg:text-left">
                <div className="bg-black/60 backdrop-blur-2xl border-2 border-[#E6C685]/40 rounded-3xl p-8 mb-8">
                  <div className="text-6xl mb-4">💫</div>
                  <h3 className="text-3xl font-bold text-[#E6C685] mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
                    Activate SpiralCred
                  </h3>
                  <p className="text-lg text-white mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Trade, trust, and thrive—no matter what happens to the dollar.
                  </p>
                  
                  <div className="space-y-3 text-left mb-6">
                    <div className="flex items-start gap-3">
                      <span className="text-[#E6C685] text-xl">✓</span>
                      <span className="text-white/80 text-sm">Credits earned through caregiving, transport, meals, and mutual aid</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-[#E6C685] text-xl">✓</span>
                      <span className="text-white/80 text-sm">Redeem for lodging, pantry items, future help, or next gathering</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-[#E6C685] text-xl">✓</span>
                      <span className="text-white/80 text-sm">Zero interest, zero bureaucracy—just gratitude with math</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-[#E6C685] text-xl">✓</span>
                      <span className="text-white/80 text-sm">Family economy that circulates inside, strengthens bonds</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={onEnterProjects}
                    className="w-full px-8 py-4 bg-gradient-to-r from-[#E6C685] to-amber-600 text-black rounded-xl font-bold text-lg hover:scale-105 transition-all"
                    style={{ fontFamily: "'Cinzel', serif" }}
                  >
                    Download SpiralOrg. Activate SpiralCred.
                  </button>
                </div>
                
                <p className="text-[#CED3CC] text-sm italic">
                  "When money breaks, trust holds. SpiralCred is how we remember."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Promise & Call */}
        <section className="relative py-24 px-6 bg-black/20">
          <div className="max-w-5xl mx-auto text-center">
            <div 
              className="bg-black/60 backdrop-blur-2xl border border-[#E6C685]/20 rounded-3xl p-12"
              style={{ boxShadow: '0 20px 80px rgba(0, 0, 0, 0.8)' }}
            >
              <h2 className="text-4xl font-light text-[#E6C685] mb-8" style={{ fontFamily: "'Cinzel', serif" }}>
                The Promise
              </h2>
              
              <p 
                className="text-2xl leading-relaxed font-light mb-12"
                style={{ 
                  fontFamily: "'Cormorant Garamond', serif",
                  color: '#CED3CC'
                }}
              >
                Every spiral intersects another. Every intersection births light. Each act of reunion—digital or physical—is a micro-creation of the world we keep saying we want.
              </p>

              <p 
                className="text-xl leading-relaxed font-medium text-white mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                SpiralOrg reminds us we are not a machine civilization; we're a <span className="text-[#E6C685] font-bold">family civilization</span>. 
                Each Library is a proof. Each project is a physics experiment in belonging. Each breath we share is the universe <span className="italic">remembering</span> how to be whole.
              </p>
              
              <div className="border-t border-[#E6C685]/30 pt-8 mt-8">
                <p 
                  className="text-base uppercase tracking-widest text-[#E6C685] mb-4"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  With belonging, we get things done
                </p>
                <p 
                  className="text-3xl font-bold text-white"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  We hyper-thrive for 10,000 generations.
                </p>
              </div>

              <div className="w-64 h-px bg-gradient-to-r from-transparent via-[#E6C685] to-transparent mx-auto mb-12"></div>

              <p className="text-xl leading-relaxed font-light text-[#CED3CC] mb-8">
                We are a <span className="text-[#E6C685]">Private Membership Association</span> operating 
                under Chapter 184 of Wisconsin law—a benevolent, lawful network formed for mutual benefit.
              </p>

              <p className="text-lg leading-relaxed font-light text-[#CED3CC] mb-12">
                Free to organize, create, and trade family to family, project to project, heart to heart. 
                You belong as long as you agree to our tenets:
              </p>

              <p 
                className="text-4xl mb-8"
                style={{ 
                  fontFamily: "'Cinzel', serif",
                  color: '#E6C685',
                  letterSpacing: '0.3em',
                  fontWeight: '300'
                }}
              >
                AGENCY · SECURITY · TRUST
              </p>

              <p className="text-lg text-[#CED3CC] italic font-light">
                And the deeper purpose: the procreation of love and wisdom for 10,000 generations.
              </p>
            </div>
          </div>
        </section>

        {/* Underground Revolution Section */}
        <section className="relative py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Shield className="w-16 h-16 text-[#E6C685] mx-auto mb-6" />
              <h2 
                className="text-5xl md:text-6xl font-light mb-8 text-white"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                An Underground Revolution
              </h2>
              <div className="w-32 h-px bg-[#E6C685] mx-auto mb-12"></div>
            </div>

            <div className="bg-gradient-to-br from-[#5C2D73]/20 to-[#E6C685]/10 backdrop-blur-2xl border border-[#E6C685]/30 rounded-3xl p-12 mb-16">
              <p className="text-2xl leading-relaxed text-[#CED3CC] mb-8 font-light text-center">
                An underground countercultural revolution of the like-minded,<br/>
                <span className="text-[#E6C685] text-3xl">seizing back our sovereignty as families</span>
              </p>
              <p className="text-xl leading-relaxed text-[#CED3CC] text-center font-light">
                As we forge serenity within self, serenity within the family, serenity reclaimed by the species. 
                Taking responsibility for our own security, and transacting in trust.
              </p>
            </div>

            {/* Professional Offramp - Spiral Organizer */}
            <div 
              onClick={onEnterProjects}
              className="group cursor-pointer bg-gradient-to-r from-black/80 to-[#1E2A38]/80 backdrop-blur-2xl 
                border-2 border-white/20 hover:border-[#E6C685] rounded-3xl p-12 transition-all duration-300 
                hover:scale-105"
              style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)' }}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 
                    className="text-4xl font-light mb-4 text-white group-hover:text-[#E6C685] transition-colors"
                    style={{ fontFamily: "'Cinzel', serif" }}
                  >
                    spiral organizer
                  </h3>
                  <p className="text-lg text-[#CED3CC] leading-relaxed mb-4">
                    The ultimate digital spiral organizer for families and teams.
                  </p>
                  <p className="text-sm text-[#E6C685]/80 font-light italic">
                    Professional-grade project management meets sacred collaboration
                  </p>
                </div>
                <ArrowRight className="w-12 h-12 text-[#E6C685] group-hover:translate-x-2 transition-transform ml-8" />
              </div>
            </div>
          </div>
        </section>

        {/* Rites of Presence - Defiance Springs */}
        <section className="relative py-32 px-6 bg-black/60">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="/defiance-springs.png" 
                  alt="Defiance Springs" 
                  className="rounded-3xl shadow-2xl border border-[#E6C685]/30"
                />
              </div>
              <div>
                <Compass className="w-14 h-14 text-[#E6C685] mb-6" />
                <h2 
                  className="text-5xl font-light mb-8 text-white"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  Rites of Presence
                </h2>
                <p className="text-2xl text-[#E6C685] mb-6 font-light">
                  A Yearlong Academy for Seekers and Seers
                </p>
                <p className="text-xl leading-relaxed text-[#CED3CC] mb-6">
                  Hosted by Lawrence Cole at the renowned <span className="text-[#E6C685]">Defiance Springs</span>—
                  the hobbit pioneer hotel lobby vibe, nestled on a 110-acre private estate in the magical Driftless region.
                </p>
                <p className="text-lg leading-relaxed text-[#CED3CC] mb-8 italic">
                  The spiralorg belonging tinker shop resort of the Seedy Peeps. 
                  Where adventure meets transformation.
                </p>
                <div className="flex gap-4">
                  <a 
                    href="https://ritesofpresence.com"
                    className="px-10 py-4 bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-full 
                      text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
                  >
                    Visit Rites of Presence
                  </a>
                  <a 
                    href="https://defiancesprings.com"
                    className="px-10 py-4 border-2 border-[#E6C685] text-[#E6C685] rounded-full 
                      text-lg hover:bg-[#E6C685] hover:text-black transition-all duration-300"
                  >
                    Defiance Springs
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Highway Quest - Bradley Section */}
        <section className="relative py-32 px-6 bg-gradient-to-b from-black/60 to-black">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <Zap className="w-14 h-14 text-[#E6C685] mb-6" />
                <h2 
                  className="text-5xl font-light mb-8 text-white"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  The Highway Quest
                </h2>
                <p className="text-2xl text-[#E6C685] mb-6 font-light">
                  An Epic Journey Across America
                </p>
                <p className="text-xl leading-relaxed text-[#CED3CC] mb-6">
                  Conscious creators on the road, supported by spiralorg. Gathering our intersecting light 
                  into a symphony of might—forging the new way of being.
                </p>
                <p className="text-lg leading-relaxed text-[#CED3CC] mb-8 italic">
                  Which is just a remembering of how we have always belonged to each other, and we always will. 
                  {/* Removed */}
                </p>
              </div>
              <div className="order-1 lg:order-2">
                <img 
                  src="/highway-quest.png" 
                  alt="Highway Quest" 
                  className="rounded-3xl shadow-2xl border border-[#E6C685]/30"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Yusuf Travel Poster - InternorTHERn */}
        <section className="relative py-32 px-6 bg-black">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <Map className="w-16 h-16 text-[#E6C685] mx-auto mb-6" />
              <h2 
                className="text-5xl md:text-6xl font-light mb-8 text-white"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                InterNorthern Tour Guides
              </h2>
              <p className="text-2xl text-[#E6C685] font-light">
                From the Driftless, North America to North Africa
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center bg-gradient-to-br from-[#5C2D73]/20 to-[#E6C685]/10 
              backdrop-blur-2xl border border-[#E6C685]/30 rounded-3xl p-12">
              <div>
                <img 
                  src="/yusuf-travel.png" 
                  alt="Youssef - Your Guide" 
                  className="rounded-2xl shadow-2xl border border-[#E6C685]/30"
                />
              </div>
              <div>
                <h3 className="text-4xl font-light mb-6 text-[#E6C685]">
                  Meet Youssef
                </h3>
                <p className="text-xl leading-relaxed text-[#CED3CC] mb-6">
                  A North African living in Viroqua—philosopher, multi-instrumentalist, man of the world, 
                  polyglot extraordinaire. <span className="text-[#E6C685]">Like living with Rumi for a time.</span>
                </p>
                <p className="text-lg leading-relaxed text-[#CED3CC] mb-6">
                  Youssef brings you into bespoke adventures exploring North African hidden jewels known only to locals—
                  this is his homeland, the land of <span className="text-[#E6C685]">the only unconquered people to ever exist.</span>
                </p>
                <p className="text-lg leading-relaxed text-[#CED3CC] mb-8">
                  But that's not all. Similar opportunities await in the magical, legendary lands of the 
                  <span className="text-[#E6C685]"> Driftless region of Wisconsin, USA</span>. Private nature adventures, 
                  tribal gatherings, music, feasting, foraging—<span className="text-[#E6C685] text-2xl font-bold">REFOUNDING!</span>
                </p>
                <button
                  className="px-10 py-4 bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-full 
                    text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  Book Your Adventure
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Feynman Quote */}
        <section className="py-24 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-[#5C2D73]/20 to-[#E6C685]/20 backdrop-blur-2xl border border-[#E6C685]/30 rounded-3xl p-16 text-center">
              <blockquote className="text-3xl md:text-4xl font-light italic text-white mb-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                "Everything is made of oscillating things."
              </blockquote>
              <p className="text-[#E6C685] text-xl mb-2">— Richard Feynman</p>
              <p className="text-[#CED3CC] text-sm">
                Nobel Prize-winning physicist who revealed the quantum dance at the heart of reality
              </p>
            </div>
          </div>
        </section>

        {/* Section removed as requested */}

        {/* Final CTA */}
        <section className="relative py-32 px-6 bg-[#1E2A38]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 
              className="text-5xl md:text-6xl font-light mb-8 text-white"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              Join the Guild
            </h2>
            <p className="text-2xl text-[#E6C685] mb-12 font-light">
              Recreate to innovate. We will be your tour guides.
            </p>

            <div className="flex flex-col md:flex-row gap-6 justify-center mb-16">
              <button
                onClick={onBeginJourney}
                className="px-12 py-5 bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-full 
                  text-xl tracking-wider hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-[#E6C685]/30"
                style={{ boxShadow: '0 10px 50px rgba(230, 198, 133, 0.5)', fontFamily: "'Cinzel', serif" }}
              >
                Begin My Journey
              </button>
              
              <button
                onClick={() => window.location.href = 'https://planetaryfed.com'}
                className="px-12 py-5 border-2 border-[#E6C685] text-[#E6C685] rounded-full 
                  text-xl tracking-wider hover:bg-[#E6C685] hover:text-black transition-all duration-300"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                Learn About the Federation
              </button>
            </div>

            {/* The Quote */}
            <div className="bg-gradient-to-r from-[#5C2D73]/20 to-[#E6C685]/20 backdrop-blur-xl border border-[#E6C685]/30 rounded-2xl p-8">
              <p className="text-lg italic text-[#CED3CC] leading-relaxed">
                "When we belong to each other, fear dissolves. When fear dissolves, trust emerges. 
                When trust emerges, the resolve always arrives. This is how reality is created through 
                consciousness, collaboration, and the courage to choose belonging."
              </p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-6 bg-black/90 border-t border-white/10">
          <div className="max-w-6xl mx-auto text-center">
            <div className="flex flex-wrap justify-center gap-6 mb-6">
              {['seedypeeps.com', 'planetaryfed.com', 'trustwoven.com', 're-inn.com', 
                'ritesofpresence.com', 'defiancesprings.com'].map((link, i) => (
                <a key={i} href={`https://${link}`}
                  className="text-[#E6C685] hover:text-white transition-colors text-sm"
                  style={{ fontFamily: "'Cinzel', serif" }}>
                  {link}
                </a>
              ))}
            </div>
            <div className="text-[#CED3CC]/60 text-xs space-y-2">
              <p className="tracking-wider text-[#E6C685]/80">
                SACRED RETREATS · FAMILY ARCHIVES · REMEDY COUNCIL
              </p>
              <p className="max-w-3xl mx-auto leading-relaxed">
                SpiralOrg · Chapter 184 Wisconsin<br/>
                Operating as a Private Membership Association for the mutual benefit of its members.
              </p>
              <p className="mt-3">© Planetary Federation of Families 2025</p>
              
              {/* Alternative Landing Page Navigation */}
              {onSwitchToCinematic && (
                <div className="mt-8 pt-8 border-t border-white/10">
                  <button
                    onClick={onSwitchToCinematic}
                    className="text-[#E6C685] hover:text-white text-sm transition-colors flex items-center gap-2 mx-auto"
                    style={{ fontFamily: "'Cinzel', serif" }}
                  >
                    <span>View Cinematic Landing Page</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-white/40 text-xs mt-2">
                    Experience the Friday Night Jam - The Quiet Rebellion of Trust
                  </p>
                </div>
              )}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default SpiralOrgFinalLanding;
